import { useEffect, useRef, useState } from 'react';
import { getResultCacheWindow } from '../../services/resultCacheApi';

const CHART_SAMPLE_SIZE = 1000;

/**
 * In windowed mode, fetch a stable first-CHART_SAMPLE_SIZE-rows sample from the
 * result cache for chart rendering. Extracted from `useResultsPanelState` to
 * keep the perf-critical readiness gate (and its rationale) self-contained.
 */
export function useChartSample(params: {
  isWindowed: boolean;
  activeCacheUri: string | undefined;
  activeRowCount: number | undefined;
  cacheGeneration: number | undefined;
  sourceGeneration: number;
}): Record<string, unknown>[] {
  const { isWindowed, activeCacheUri, activeRowCount, cacheGeneration, sourceGeneration } = params;

  const [chartBufferRows, setChartBufferRows] = useState<Record<string, unknown>[]>([]);
  const activeCacheUriRef = useRef(activeCacheUri);
  activeCacheUriRef.current = activeCacheUri;
  const cacheGenerationRef = useRef(cacheGeneration);
  cacheGenerationRef.current = cacheGeneration;

  // Readiness gate for the chart sample. Deliberately NOT keyed on the live
  // `activeRowCount`: during a windowed stream `activeRowCount`
  // (= populationEntry.loadedRowCount) increments on EVERY progress batch, so
  // depending on it re-fired this effect hundreds of times for a 100K query
  // (thousands for 2M) — each firing a redundant getResultCacheWindow(1000)
  // IPC round-trip + setChartBufferRows re-render. That self-inflicted
  // IPC/render amplification intermittently wedged the renderer JS thread under
  // accumulated multi-tab load (chart view isn't even shown mid-stream). The
  // first CHART_SAMPLE_SIZE rows are stable as more rows append, so a single
  // fetch per (uri, generation) is correct. Windowed mode only engages well
  // above CHART_SAMPLE_SIZE, so this boolean flips false→true exactly once per
  // query instead of ticking per batch. Sort/filter (sourceGeneration) and
  // re-exec (cacheGeneration) still retrigger via the other deps.
  const chartSampleReady = isWindowed && (activeRowCount ?? 0) >= CHART_SAMPLE_SIZE;
  useEffect(() => {
    if (!chartSampleReady || !activeCacheUri) {
      setChartBufferRows([]);
      return;
    }
    const capturedGeneration = sourceGeneration;
    const uriAtFetchTime = activeCacheUri;
    const genAtFetchTime = cacheGeneration;
    getResultCacheWindow({
      uri: uriAtFetchTime,
      startIndex: 0,
      count: CHART_SAMPLE_SIZE,
      expectedGeneration: capturedGeneration,
    }).then((res) => {
      if (activeCacheUriRef.current !== uriAtFetchTime) return;
      if (cacheGenerationRef.current !== genAtFetchTime) return;
      if (res.generation >= capturedGeneration) {
        setChartBufferRows(res.rows);
      }
    }).catch(() => setChartBufferRows([]));
  }, [chartSampleReady, activeCacheUri, cacheGeneration, sourceGeneration]);

  return chartBufferRows;
}
